import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessagesSquareIcon, SearchIcon, XIcon } from 'lucide-react';
import { mag, type MeetingSamenvatting } from '@vip/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Empty, EmptyContent, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '../auth/useAuth';
import { GesprekKaart } from './GesprekKaart';
import { OpnameKnoppen } from './OpnameKnoppen';
import { NieuwGesprek } from './NieuwGesprek';
import { useGesprekken, useVerwijderGesprek } from './gesprekken.api';
import { WACHT_OP_BUDDY, datum, weergave } from './gesprekstand';
import { filter, groepeerPerJaar } from './zoeken';

/** Een gesprek dat op de buddy wacht of dat is mislukt. Het tabje van een jaar
 *  waarin zoiets staat zegt dat, want anders moet je elk jaar langs om te zien
 *  of je nog iets te doen hebt. */
const vraagtAandacht = (g: MeetingSamenvatting) =>
  WACHT_OP_BUDDY.includes(weergave(g)) || g.status === 'FAILED';

/**
 * De lijst met eigen gesprekken, met een tabje per jaar.
 *
 * Geen eigen kop: de balk bovenaan noemt het scherm al. Wat hier bovenaan staat
 * is terugvinden wat je zoekt, en er een bij zetten.
 *
 * Er stond ook een badge met hoeveel gesprekken op je wachten. Die is weg: dat
 * getal staat al op het tabje van het jaar waar ze in zitten, en de kaarten zelf
 * lichten op met "Jij bent aan zet" erop. Drie keer hetzelfde melden maakt het
 * niet dringender.
 *
 * Een tabje per jaar, want er worden in de praktijk maximaal een twaalftal
 * gesprekken per jaar opgenomen. Eén jaar is dus precies één lijst die je in één
 * blik overziet, en er valt binnen dat jaar niets meer te groeperen of in te
 * klappen.
 *
 * Het scherm is van iedereen met een inlog, het aanleveren van het buddyvinkje.
 * Wie dat vinkje niet heeft ziet dezelfde lijst, met de twee knoppen
 * uitgeschakeld en de reden eronder. Die lijst is leeg zolang hij niets heeft
 * aangeleverd.
 */
export function MijnGesprekken() {
  const { user } = useAuth();
  const magAanleveren = mag(user?.rechten, 'meeting:create');
  const locatie = useLocation();
  const navigate = useNavigate();
  const { data: gesprekken, isLoading } = useGesprekken();
  const verwijder = useVerwijderGesprek();
  /** Het venster staat open zodra er een opname is: die komt van de knop
   *  hiernaast, van de opnamepagina, of van de opname-app van een telefoon. */
  const [nieuw, setNieuw] = useState<{
    bestand?: File;
    titel?: string;
    datum?: string;
    metOpname?: boolean;
  } | null>(null);
  const [teVerwijderen, setTeVerwijderen] = useState<MeetingSamenvatting | null>(null);
  const [zoekterm, setZoekterm] = useState('');
  const [gekozenJaar, setGekozenJaar] = useState<string>();

  /**
   * De opname die van de opnamepagina komt. Die reist in de routerstaat mee en
   * niet in een store: het bestand bestaat alleen in dit tabblad. Meteen uit de
   * geschiedenis halen, anders staat het venster er bij elke stap terug weer.
   */
  useEffect(() => {
    const staat = locatie.state as { opname?: File; titel?: string; datum?: string } | null;
    if (!staat?.opname) return;
    setNieuw({ bestand: staat.opname, titel: staat.titel, datum: staat.datum });
    navigate('/gesprekken', { replace: true, state: null });
  }, [locatie.state, navigate]);

  const zoekt = zoekterm.trim().length > 0;
  const jaren = groepeerPerJaar(filter(gesprekken ?? [], zoekterm));

  /**
   * Het gekozen jaar, of het nieuwste jaar dat er nog is.
   *
   * Afgeleid en niet in een effect gelijkgetrokken. Bij het zoeken kan het jaar
   * waar je op staat uit de lijst verdwijnen, en dan hoor je de treffers te zien
   * die er wél zijn in plaats van een leeg tabblad.
   */
  const actiefJaar = jaren.find((j) => j.jaar === gekozenJaar)?.jaar ?? jaren[0]?.jaar;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-3">
        <InputGroup className="w-full sm:max-w-xs">
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
          <InputGroupInput
            aria-label="Zoek in je gesprekken"
            onChange={(e) => setZoekterm(e.target.value)}
            placeholder="Zoek op naam of datum"
            value={zoekterm}
          />
          {zoekt && (
            <InputGroupAddon align="inline-end">
              <InputGroupButton aria-label="Zoekterm wissen" onClick={() => setZoekterm('')}>
                <XIcon />
              </InputGroupButton>
            </InputGroupAddon>
          )}
        </InputGroup>

        <OpnameKnoppen
          className="ml-auto"
          magAanleveren={magAanleveren}
          onOpnemen={() => setNieuw({ metOpname: true })}
          onVenster={() => setNieuw({})}
        />
      </div>

      {isLoading && !gesprekken ? (
        <div className="flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <Skeleton className="h-44 w-full" key={i} />
          ))}
        </div>
      ) : jaren.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              {zoekt ? <SearchIcon /> : <MessagesSquareIcon />}
            </EmptyMedia>
            <EmptyTitle>{zoekt ? 'Niets gevonden' : 'Nog geen gesprekken'}</EmptyTitle>
          </EmptyHeader>
          {/* Alleen bij zoeken een knop. Zonder gesprekken staan Opnemen en
              Bestand kiezen al bovenaan; ze hier herhalen maakt van een lege
              lijst een tweede aanbod. */}
          {zoekt && (
            <EmptyContent>
              <Button onClick={() => setZoekterm('')} variant="outline">
                Zoekterm wissen
              </Button>
            </EmptyContent>
          )}
        </Empty>
      ) : (
        <Tabs onValueChange={setGekozenJaar} value={actiefJaar}>
          {/* Eén tabje is geen keuze, dus dan geen balk. Zodra het tweede jaar
              begint staat hij er vanzelf. */}
          {jaren.length > 1 && (
            <TabsList>
              {jaren.map((jaar) => {
                const wacht = jaar.gesprekken.filter(vraagtAandacht).length;
                return (
                  <TabsTrigger key={jaar.jaar} value={jaar.jaar}>
                    {jaar.jaar}
                    {/* Alleen dit getal, en alleen als er iets op je wacht. Er
                        stond ook het aantal gesprekken in het jaar naast, en dan
                        las "2026 64" als één getal: twee getallen naast elkaar
                        zonder dat er iets bij staat wat ze betekenen. Het aantal
                        is bovendien het minst nuttige van de twee, want de lijst
                        eronder toont het al. Een getal op een tabje betekent hier
                        dus altijd hetzelfde: zoveel wachten op jou. */}
                    {wacht > 0 && (
                      <Badge aria-label={`${wacht} ${wacht === 1 ? 'wacht' : 'wachten'} op jou`}>
                        {wacht}
                      </Badge>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          )}

          {jaren.map((jaar) => (
            <TabsContent className="flex flex-col gap-4" key={jaar.jaar} value={jaar.jaar}>
              {jaar.gesprekken.map((g) => (
                <GesprekKaart gesprek={g} key={g.id} onVerwijder={() => setTeVerwijderen(g)} />
              ))}
            </TabsContent>
          ))}
        </Tabs>
      )}

      {nieuw && (
        <NieuwGesprek begin={nieuw} metOpname={nieuw.metOpname} onSluit={() => setNieuw(null)} />
      )}

      <AlertDialog
        onOpenChange={(open) => !open && setTeVerwijderen(null)}
        open={teVerwijderen !== null}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              “{teVerwijderen?.titel ?? (teVerwijderen && datum(teVerwijderen.occurredAt))}”
              verwijderen?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Dit verwijdert de opname, het transcript, de samenvatting en de hulpvragen van dit
              gesprek definitief. Er is geen prullenbak en geen herstel; ook een Admin kan het niet
              terughalen. Onderwerpen die ook in andere gesprekken voorkomen blijven bestaan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => teVerwijderen && verwijder.mutate(teVerwijderen.id)}
              variant="destructive"
            >
              Ja, definitief verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
